import { useEffect, useState } from 'preact/hooks';
import {
  getRecordTypeSchema,
  listAllRecordsByType,
  listAllRecordsWithLinks,
  type LinkRow,
  type RecordRow,
  type RecordTypeSchema,
  type RecordsWithLinks,
} from '../lib/api';
import { useRecordsVersion } from '../lib/recordEditor';
import { ListSearch, matches } from '../components/ListSearch';
import { ListSort, type SortDir, type SortOption } from '../components/ListSort';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import './WorkOrders.css';

// S1 — THE WORK ORDER LIST: the spine. Rows are the blessed prototype's
// own rendering (wf02's .row: stripe · name+context · money+state); each
// row is a doorway to the WO's own route. The lifecycle filter's
// vocabulary comes from the MANIFEST (the fetched hvac_work_order schema),
// never a hardcoded status list — the pack owns those words.
//
// S7 (Part D, Huan chose A+B): fifteen chips in a row took the whole
// phone, so the filter reads as the four PILES the office actually thinks
// in — Needs a tech / In progress / Awaiting money / Closed — each with a
// COUNT (wf02's own money-band argument: a number you haven't touched yet
// is already information). The full manifest vocabulary stays one tap
// away: "All" opens the complete per-state chip list, and a selected pile
// shows its own member states as chips, so the manifest's words never
// disappear. Pile keys are HYPHENATED where the manifest's states use
// underscores, so the two vocabularies cannot collide in the route's
// opaque `state` segment: /work_orders/awaiting_payment (S1's money-band
// links) still means the STATE, /work_orders/awaiting-money is the pile —
// purely additive. A state the manifest grows later that this grouping
// doesn't know stays reachable and counted under All rather than being
// silently misfiled into a pile.
//
// ORDER: newest first. Alan's triage rule (life-safety → freezers →
// experience) needs a priority taxonomy, and on this port `priority` is
// deliberately PLAIN TEXT (the fork's H1/H2/H3 picklist was invented — see
// recordTypes.ts's own field note); until Alan's real H-code taxonomy
// lands there is no field to triage by, so recency is the honest order.
// The `urgent` flag still speaks: it colours the row's stripe.
//
// ---------------------------------------------------------------------
// S12a — THE ROW NOW SAYS WHOSE ORDER IT IS.
//
// Huan, 2026-08-12: *"When we see the work order screen, it's impossible to
// find anything. Each work order should have the client and location name
// and we need to be able to sort by those."* S1 shipped rows as
// stripe · name · money · state and never revisited that once the tenant
// held 204 of them; neither `wo_client` nor `wo_site` appeared here at all.
//
// THE JOIN. `hvac_work_order` carries NO denormalized client or site name —
// checked against the pack (recordTypes.ts): the relationship is links,
// `wo_client` and `wo_site`, and hvac_site holds its own master_detail
// `client`. A row still being read says "reading…", a row whose read FAILED
// says so, and a row that genuinely has no link says THAT — three different
// truths a blank cell would collapse into one lie. Nothing here infers a
// client from a title or an address.
//
// ---------------------------------------------------------------------
// S15 — THE ~250 REQUESTS ARE GONE, AND A REFUSED READ NO LONGER READS AS
// AN EMPTY SHOP.
//
// S12a shipped this join as a client-side fan-out because there was no bulk
// link read: one `GET /records/:id?include=links` per site (46) and one per
// work order (204) — ~250 requests at concurrency 10, capped at
// WO_LINK_CAP with an on-screen warning when the cap bit. On 2026-08-13 that
// cost stopped being theoretical: the global limiter allows 300 requests per
// minute per IP, so opening THIS ONE SCREEN spent 83% of a real user's whole
// budget and the screens he opened next were refused — and rendered the
// refusal as "no records", which is how a rate limit came to tell a man his
// shop was empty.
//
// S15 gave `GET /records` the `?include=links` the single-record door always
// had. The join is now:
//
//   the orders   listAllRecordsWithLinks(hvac_work_order,
//                [wo_client, wo_site])     → 2 requests (204 rows, 200/page)
//   the schema   getRecordTypeSchema                       → 1 request
//   the names    hvac_client list + hvac_site WITH its own
//                `client` link                             → 2 requests
//
// FIVE requests where there were ~250, and NO CAP — the link expansion rides
// the page it belongs to, so it costs nothing per row. `link_type` names the
// two relationships this screen renders, so an order's other links (visits,
// invoices, notes) are never fetched and the per-record cap is never in
// sight.
//
// THE SPINE STILL DOES NOT WAIT FOR THE NAMES: rows, piles and counts paint
// from the first read; the two name lists follow and the cells fill in. And
// every read on this screen now fails OUT LOUD — a refused list says which
// refusal it was (a 429 says it is temporary), and a name read that fails
// leaves the rows on screen saying they could not be read, rather than
// blanking into "no client".

export interface WorkOrdersProps {
  tenantId: string;
  /** Route-carried filter, opaque in the route: a manifest lifecycle
   *  state (S1's contract, unchanged) or an S7 pile key. */
  state?: string;
  onOpen: (id: string) => void;
  onFilter: (state?: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

const WO_TYPE = 'hvac_work_order';
const CLIENT_TYPE = 'hvac_client';
const SITE_TYPE = 'hvac_site';

/** The relationships this screen renders, named in the request so the server
 *  sends two links per order instead of every link it has. */
const WO_LINK_TYPES = ['wo_client', 'wo_site'];
/** hvac_site's own master_detail parent — the fallback for an order that
 *  links a site but no client (the V6 import's shape). */
const SITE_CLIENT_LINK = 'client';

/** What this screen knows about one order's client and site. Four states,
 *  because "we have not looked yet", "we could not look", "there is no link"
 *  and "here is the name" are four different things and only the last one
 *  is a name. */
type Party =
  | { kind: 'pending' }
  | { kind: 'beyond' }
  | { kind: 'unread' }
  | {
      kind: 'known';
      /** The name, when the link exists AND the record came back in the
       *  tenant's own client/site list. */
      client: string | null;
      site: string | null;
      /** Whether the LINK exists at all — the difference between "this
       *  order has no client" and "it has one we could not name". */
      clientLinked: boolean;
      siteLinked: boolean;
    };

const PENDING: Party = { kind: 'pending' };
const UNREAD: Party = { kind: 'unread' };
/** S15 keeps this state and re-anchors it. It used to mean "past the
 *  fan-out cap"; there is no cap now, so it means the only remaining way an
 *  order can be on screen without an answer about its links: the list read
 *  came back without a link expansion for it at all (an old server, or the
 *  4,000-row page ceiling in listAllRecordsWithLinks). Still "not read",
 *  still not a blank. */
const NOT_EXPANDED: Party = { kind: 'beyond' };

/** The name a sort or a search can use — null for every state that is not
 *  a name, which is what puts those rows at the end of an ordering instead
 *  of scattered through it under an empty string. */
function partyName(p: Party | undefined, which: 'client' | 'site'): string | null {
  if (p === undefined || p.kind !== 'known') return null;
  return which === 'client' ? p.client : p.site;
}

/** The other end of a relationship, whichever side of the link row this
 *  record sits on (same helper S3's board uses). */
function linkedId(links: LinkRow[], selfId: string, linkType: string): string | null {
  for (const l of links) {
    if (l.link_type !== linkType) continue;
    if (l.src_id === selfId) return l.dst_id;
    if (l.dst_id === selfId) return l.src_id;
  }
  return null;
}

/** The whole join, as a pure function of the three reads. Every branch
 *  answers with one of the four truths — there is no path here that returns
 *  a name it did not read, and none that returns nothing at all. */
function resolveParties(
  orders: readonly RecordRow[],
  orderLinks: RecordsWithLinks,
  clients: readonly RecordRow[],
  siteLinks: RecordsWithLinks
): Map<string, Party> {
  const clientName = new Map(clients.map((c) => [c.id, c.display_name]));
  const siteName = new Map(siteLinks.records.map((s) => [s.id, s.display_name]));

  // An id the server would not answer for, or whose link set it had to cut
  // short, is one we cannot make a claim about: a missing `wo_client` in a
  // truncated set is not evidence there is no client.
  const orderUnanswered = new Set([...orderLinks.unreadable, ...orderLinks.truncated]);
  const siteUnanswered = new Set([...siteLinks.unreadable, ...siteLinks.truncated]);

  const siteClient = new Map<string, string | null>();
  for (const s of siteLinks.records) {
    if (siteUnanswered.has(s.id)) continue;
    siteClient.set(s.id, linkedId(siteLinks.linksByRecordId[s.id] ?? [], s.id, SITE_CLIENT_LINK));
  }

  const map = new Map<string, Party>();
  for (const wo of orders) {
    if (orderUnanswered.has(wo.id)) {
      map.set(wo.id, UNREAD);
      continue;
    }
    const links = orderLinks.linksByRecordId[wo.id];
    if (links === undefined) {
      map.set(wo.id, NOT_EXPANDED);
      continue;
    }
    const siteId = linkedId(links, wo.id, 'wo_site');
    const clientId =
      linkedId(links, wo.id, 'wo_client') ?? (siteId ? (siteClient.get(siteId) ?? null) : null);
    map.set(wo.id, {
      kind: 'known',
      clientLinked: clientId !== null,
      siteLinked: siteId !== null,
      client: clientId !== null ? (clientName.get(clientId) ?? null) : null,
      site: siteId !== null ? (siteName.get(siteId) ?? null) : null,
    });
  }
  return map;
}

function cmpText(a: string, b: string): number {
  return a.localeCompare(b, 'en-CA', { sensitivity: 'base', numeric: true });
}

const SORTS: readonly SortOption[] = [
  { key: 'recent', label: 'Raised', ascWord: 'oldest', descWord: 'newest', defaultDir: 'desc' },
  { key: 'client', label: 'Client', ascWord: 'A–Z', descWord: 'Z–A', defaultDir: 'asc' },
  { key: 'site', label: 'Location', ascWord: 'A–Z', descWord: 'Z–A', defaultDir: 'asc' },
];

// The piles' MEMBERSHIP is this screen's own grouping of the manifest's
// states (the office's four questions); the words shown on the state chips
// themselves still come from the fetched schema, and a pile only ever
// claims states the schema actually declares (see the intersection below).
const PILES: readonly { key: string; label: string; states: readonly string[] }[] = [
  { key: 'needs-a-tech', label: 'Needs a tech', states: ['intake', 'unassigned'] },
  {
    key: 'in-progress',
    label: 'In progress',
    states: [
      'in_progress',
      'parts_research',
      'awaiting_quote_prep',
      'awaiting_office_approval',
      'awaiting_customer_approval',
      'awaiting_supervisor_approval',
    ],
  },
  {
    key: 'awaiting-money',
    label: 'Awaiting money',
    states: ['awaiting_invoicing', 'awaiting_payment', 'awaiting_deposit'],
  },
  { key: 'closed', label: 'Closed', states: ['deposited', 'done', 'warranty', 'cancelled'] },
];

function humanize(key: string): string {
  return key.replace(/_/g, ' ');
}

function money(n: number): string {
  return n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function relativeWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.round((Date.now() - then) / 86_400_000);
  if (days < 1) return 'today';
  if (days === 1) return '1d';
  return `${days}d`;
}

export function WorkOrders({ tenantId, state, onOpen, onFilter }: WorkOrdersProps) {
  const [load, setLoad] = useState<LoadState>('loading');
  // S15: the FAILURE, not a message. A read that was refused says which
  // refusal it was (and, for a 429, that it is temporary) — the screen never
  // renders a failure as an empty shop.
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [rows, setRows] = useState<RecordRow[]>([]);
  const [schema, setSchema] = useState<RecordTypeSchema | null>(null);
  // S7: whether "All" has been tapped open to show the full per-state chip
  // list (the one tap the full vocabulary sits behind).
  const [statesOpen, setStatesOpen] = useState(false);
  // S12a — the client/site join. S15: one bulk read, so this is no longer a
  // progress bar over hundreds of requests, only "the names are still on
  // their way" and, if they never arrive, why.
  const [party, setParty] = useState<Map<string, Party>>(() => new Map());
  const [joining, setJoining] = useState(false);
  const [namesFailure, setNamesFailure] = useState<ReadFailure | null>(null);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('recent');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  // Bumped by the retry affordance the failure states offer. A 429 is
  // temporary and a retry genuinely works, so the screen offers one rather
  // than making the user find the reload.
  const [retryNonce, setRetryNonce] = useState(0);
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    let cancelled = false;

    async function loadAll() {
      setLoad('loading');
      setFailure(null);
      setNamesFailure(null);

      // THE SPINE — the orders, their two links, and the manifest's own
      // lifecycle vocabulary. S7: the whole set, unfiltered, because the
      // pile COUNTS need every row regardless of the active filter (the
      // filter is applied client-side below, so switching is instant).
      let orderLinks: RecordsWithLinks;
      let sch: RecordTypeSchema;
      try {
        [orderLinks, sch] = await Promise.all([
          listAllRecordsWithLinks(tenantId, WO_TYPE, { linkTypes: WO_LINK_TYPES }),
          getRecordTypeSchema(tenantId, WO_TYPE),
        ]);
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setLoad('error');
        return;
      }
      if (cancelled) return;

      // The engine lists oldest-first (its own documented ORDER BY); the
      // spine reads newest-first — see the header on why recency, not
      // priority, is the order this port can honestly claim.
      //
      // S11 Part B: RETIRED orders leave the spine and every pile count with
      // it. A pile is a question about live work ("what needs a tech"), and
      // core's listRecords only excludes 'deleted', so an archived order was
      // still being counted into the answer. Retired orders stay reachable
      // by their own route and from the client/site they hang off.
      const orders = activeOnly(orderLinks.records).reverse();
      setRows(orders);
      setSchema(sch);
      setLoad('ready');
      if (orders.length === 0) {
        setParty(new Map());
        setJoining(false);
        return;
      }

      // THE NAMES — two more reads, after the rows are already on screen.
      // The client list for its names; the site list WITH its own `client`
      // link, which is what lets an order that links a site but no client
      // still say whose it is (the V6 import links wo_site for every row and
      // wo_client only where the sheet carried a client ref).
      setParty(new Map(orders.map((o) => [o.id, PENDING])));
      setJoining(true);
      try {
        const [clients, siteLinks] = await Promise.all([
          listAllRecordsByType(tenantId, CLIENT_TYPE),
          listAllRecordsWithLinks(tenantId, SITE_TYPE, { linkTypes: [SITE_CLIENT_LINK] }),
        ]);
        if (cancelled) return;
        setParty(resolveParties(orders, orderLinks, clients, siteLinks));
        setJoining(false);
      } catch (err) {
        if (cancelled) return;
        // The orders are real and stay on screen. What we could not read is
        // WHOSE they are, and every cell says exactly that instead of
        // sitting on "reading…" forever or blanking into "no client".
        setNamesFailure(describeReadFailure(err));
        setParty(new Map(orders.map((o) => [o.id, UNREAD])));
        setJoining(false);
      }
    }

    void loadAll();
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordsVersion, retryNonce]);

  if (load === 'loading') {
    return (
      <div class="work-orders">
        <div class="work-orders__eyebrow">Work orders</div>
        <div class="work-orders__say">One moment…</div>
      </div>
    );
  }

  if (load === 'error' && failure !== null) {
    // NOT an empty state. The 2026-08-13 walk showed what the alternative
    // costs: a refused read rendered as "no records" told a man his shop was
    // empty. The headline says what happened, the second line says what is
    // true right now, and a retryable failure gets a door out.
    return (
      <div class="work-orders">
        <div class="work-orders__eyebrow">Work orders</div>
        <div class="work-orders__say">
          {failure.headline}
          <br />
          <span class="work-orders__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="work-orders__retry" onClick={() => setRetryNonce((n) => n + 1)}>
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  const states = schema?.lifecycle?.states ?? [];

  // The piles this schema can actually claim: membership intersected with
  // the fetched state list, and a pile with no surviving member disappears
  // rather than showing an impossible door.
  const piles = PILES.map((p) => ({
    key: p.key,
    label: p.label,
    states: p.states.filter((s) => states.includes(s)),
  })).filter((p) => p.states.length > 0);

  // What the route's opaque `state` means here: nothing (All), one of the
  // pile keys, or a manifest state (S1's unchanged contract — the admin
  // home's money bands link straight to these).
  const activePile = state !== undefined ? piles.find((p) => p.key === state) : undefined;
  const activeState = state !== undefined && activePile === undefined ? state : undefined;
  // The pile the current selection lives in — lit even when the selection
  // is a single state, so the two rows always agree about where you are.
  const litPile =
    activePile ?? (activeState !== undefined ? piles.find((p) => p.states.includes(activeState)) : undefined);

  const countByState = new Map<string, number>();
  for (const r of rows) {
    const s = r.lifecycle_state ?? '';
    countByState.set(s, (countByState.get(s) ?? 0) + 1);
  }
  const pileCount = (p: { states: string[] }) =>
    p.states.reduce((sum, s) => sum + (countByState.get(s) ?? 0), 0);

  const inPile =
    activeState !== undefined
      ? rows.filter((r) => r.lifecycle_state === activeState)
      : activePile !== undefined
        ? rows.filter((r) => activePile.states.includes(r.lifecycle_state ?? ''))
        : rows;

  // S12a — SEARCH, then SORT, over the pile the filter chose. The piles stay
  // the filter (S7); these two are how you find one row inside one.
  const searching = query.trim().length > 0;
  const found = searching
    ? inPile.filter((r) => {
        const p = party.get(r.id);
        return matches(query, [
          r.display_name,
          typeof r.fields['wo_number'] === 'string' ? (r.fields['wo_number'] as string) : '',
          typeof r.fields['title'] === 'string' ? (r.fields['title'] as string) : '',
          partyName(p, 'client'),
          partyName(p, 'site'),
        ]);
      })
    : inPile;

  // `rows` is already newest-first, so 'recent' descending is the array as
  // it stands. A row whose client or site is not a NAME yet — still being
  // read, past the cap, unreadable, or genuinely unlinked — sorts to the end
  // in both directions rather than pretending to a position among the names.
  const visible = (() => {
    if (sortKey === 'recent') return sortDir === 'desc' ? found : [...found].reverse();
    const which = sortKey === 'client' ? 'client' : 'site';
    const named: RecordRow[] = [];
    const unnamed: RecordRow[] = [];
    for (const r of found) (partyName(party.get(r.id), which) !== null ? named : unnamed).push(r);
    named.sort((a, b) =>
      cmpText(partyName(party.get(a.id), which) ?? '', partyName(party.get(b.id), which) ?? '')
    );
    if (sortDir === 'desc') named.reverse();
    return [...named, ...unnamed];
  })();

  // The second chip row — the manifest's own words, one tap away: a lit
  // pile shows its member states; "All" (or a filter no pile claims) shows
  // the complete list once it has been tapped open.
  const chipStates =
    litPile !== undefined ? litPile.states : statesOpen || activeState !== undefined ? states : [];

  const filterLabel = activeState !== undefined ? humanize(activeState) : activePile?.label;

  return (
    <div class="work-orders">
      <div class="work-orders__eyebrow">Work orders</div>
      <h2 class="work-orders__title">
        {visible.length === 1 ? '1 work order' : `${visible.length} work orders`}
        {filterLabel !== undefined ? (
          <span class="work-orders__title-state"> · {filterLabel}</span>
        ) : null}
        {searching ? (
          <span class="work-orders__title-state"> · matching “{query.trim()}”</span>
        ) : null}
      </h2>

      {rows.length > 0 ? (
        <ListSearch
          value={query}
          onChange={setQuery}
          label="Search work orders by client, location, title or number"
          placeholder="Search client, location, title or WO number"
        />
      ) : null}

      {states.length > 0 ? (
        <>
          <div class="work-orders__filters" role="group" aria-label="Filter by pile">
            {piles.map((p) => (
              <button
                type="button"
                key={p.key}
                class={
                  litPile?.key === p.key
                    ? 'work-orders__pill work-orders__pill--on'
                    : 'work-orders__pill'
                }
                onClick={() => {
                  setStatesOpen(false);
                  onFilter(p.key);
                }}
              >
                {p.label}
                <b class="work-orders__pill-n">{pileCount(p)}</b>
              </button>
            ))}
            <button
              type="button"
              class={
                state === undefined || (activeState !== undefined && litPile === undefined)
                  ? 'work-orders__pill work-orders__pill--on'
                  : 'work-orders__pill'
              }
              onClick={() => {
                // The door to the full per-state list: first tap clears any
                // filter and opens it, a second tap folds it away again.
                setStatesOpen(state === undefined ? !statesOpen : true);
                onFilter(undefined);
              }}
            >
              All
              <b class="work-orders__pill-n">{rows.length}</b>
            </button>
          </div>
          {chipStates.length > 0 ? (
            <div
              class="work-orders__filters work-orders__filters--states"
              role="group"
              aria-label="Filter by state"
            >
              {chipStates.map((s) => (
                <button
                  type="button"
                  key={s}
                  class={
                    activeState === s ? 'work-orders__pill work-orders__pill--on' : 'work-orders__pill'
                  }
                  onClick={() => onFilter(s)}
                >
                  {humanize(s)}
                </button>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      {rows.length > 0 ? (
        <ListSort
          options={SORTS}
          activeKey={sortKey}
          dir={sortDir}
          onChange={(key, dir) => {
            setSortKey(key);
            setSortDir(dir);
          }}
        />
      ) : null}

      {/* The join, saying where it is. It disappears when it is done — and
          while it runs, the client and location cells say "reading…" rather
          than showing an empty space that would read as "no client". */}
      {joining ? <div class="work-orders__note">Reading client and location…</div> : null}
      {namesFailure !== null ? (
        // The orders below are real; their client and location are not
        // missing, they are UNREAD, and the difference is the whole point.
        <div class="work-orders__note work-orders__note--warn">
          <b>{namesFailure.headline}</b> {namesFailure.detail} The orders below are real — it is the
          client and location that went unread.
          {namesFailure.retryable ? (
            <>
              {' '}
              <button
                type="button"
                class="work-orders__retry work-orders__retry--inline"
                onClick={() => setRetryNonce((n) => n + 1)}
              >
                Read it again
              </button>
            </>
          ) : null}
        </div>
      ) : null}

      {visible.length === 0 ? (
        <div class="work-orders__empty">
          {searching
            ? `Nothing here matches “${query.trim()}”.`
            : filterLabel !== undefined
              ? `Nothing in ${filterLabel}.`
              : 'No work orders yet.'}
        </div>
      ) : (
        <div class="work-orders__list">
          {visible.map((r) => {
            const total = Number(r.fields['invoice_total_sum'] ?? 0);
            const urgent = r.fields['urgent'] === true;
            const jobType = typeof r.fields['job_type'] === 'string' ? (r.fields['job_type'] as string) : '';
            const p = party.get(r.id) ?? PENDING;
            return (
              <button type="button" class="work-orders__row" key={r.id} onClick={() => onOpen(r.id)}>
                <span
                  class="work-orders__stripe"
                  style={{ background: urgent ? 'var(--status-critical)' : 'transparent' }}
                />
                <span class="work-orders__main">
                  {/* WHOSE ORDER IT IS, first — the question this screen
                      could not answer before S12a. Client leads; the
                      location follows it on one line on a wide screen and
                      drops to its own line on a phone. */}
                  <b class="work-orders__who">
                    {partyCell(p, 'client')}
                    <span class="work-orders__where">
                      {/* A REAL separator, not a CSS ::before: the row's text
                          is what gets copied, read aloud and searched, and it
                          should read the way it looks. It folds away on a
                          phone, where the location takes its own line. */}
                      <span class="work-orders__sep"> · </span>
                      {partyCell(p, 'site')}
                    </span>
                  </b>
                  <small>
                    {r.display_name}
                    {jobType ? ` · ${humanize(jobType)}` : ''}
                    {` · ${relativeWhen(r.created_at)}`}
                  </small>
                </span>
                <span class="work-orders__right">
                  {Number.isFinite(total) && total > 0 ? `$${money(total)}` : '—'}
                  <span class="work-orders__st">{humanize(r.lifecycle_state ?? '')}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** One cell of the row's "whose is it" line. Every branch is a different
 *  TRUTH and each says its own: the name, "no client linked" when the record
 *  really carries no link, "reading…" while the fan-out is still walking,
 *  "not read" past the cap, "couldn't read" when the request failed. There is
 *  no branch that renders nothing, and none that guesses a name. */
function partyCell(p: Party, which: 'client' | 'site') {
  const noun = which === 'client' ? 'client' : 'location';
  if (p.kind === 'pending') return <i class="work-orders__unk">reading…</i>;
  if (p.kind === 'beyond') return <i class="work-orders__unk">{noun} not read</i>;
  if (p.kind === 'unread') return <i class="work-orders__unk">couldn't read the {noun}</i>;
  const name = which === 'client' ? p.client : p.site;
  if (name !== null) return name;
  const linked = which === 'client' ? p.clientLinked : p.siteLinked;
  return <i class="work-orders__unk">{linked ? `${noun} not named` : `no ${noun} linked`}</i>;
}
